import {
	ReactElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	FC,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { format } from 'date-fns';
import Offline from './Offline';
import ErrorBoundary from './ErrorBoundary';
import { useCAWContext } from '../CAW';
import CAWMobileDialogContent from './CAWMobileDialogContent';
import { useScrollDirection } from '@fshr/hooks';
import { translate, triggerEvent } from '@micro-apps/env';
import { AvailabilityStore } from '@micro-apps/stores';
import { observer } from 'mobx-react-lite';
import Button from '../../button';
import { IconCaretDown, IconLocationPinLargeFill, IconX } from '../../icon';
import BevSafeText from '../../bev-safe-text';
import LoadingIndicator from '../../loading-indicator';
import Modal from '../../modal';

type Props = {
	submitSearch: () => void;
	availabilityStore: AvailabilityStore;
	propertiesArray?: string[];
	lockedStep?: number;
	destinationPlaceholder?: string;
	ppMode?: boolean;
	/** Mirrors the desktop `shouldHidePromoCode` gate so both layouts agree. */
	hidePromoCode?: boolean;
	EditButton?: FC<{ open?: () => void }>;
};

function CAWMobileDialog({
	submitSearch,
	availabilityStore,
	propertiesArray = [],
	lockedStep,
	destinationPlaceholder,
	ppMode = false,
	hidePromoCode = false,
	EditButton,
}: Props): ReactElement<any> {
	const cawComponentRef = useRef(null);
	const {
		planStore,
		isSingleProperty,
		isPPC,
		timezone,
		isPropertySubpage,
		isDesktop,
		isABF,
		isHeroVariant,
	} = useCAWContext();
	const [editSelectionMode, setEditSelectionMode] = useState(!!lockedStep);
	const [portalTarget, setPortalTarget] = useState<Element | null>(null);
	const loading = planStore?.fetching && !isSingleProperty && !timezone && !isHeroVariant;
	const isOffline = planStore?.offline();
	const startingStep = useRef(
		lockedStep && !isNaN(lockedStep)
			? lockedStep
			: isSingleProperty
				? 2
				: planStore?.search?.hotelCode
					? 4
					: 1
	);
	const [step, setStepState] = useState(startingStep.current);
	const [prevStep, setPrevStep] = useState(startingStep.current);
	const [transitioningStep, setTransitioningStep] = useState(startingStep.current);
	const [error, setError] = useState(true);
	const mainBodyRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);
	const updateButtonId = `caw-update-button-${lockedStep}`;
	const scrollDirection = useScrollDirection();
	const [modalOpen, setModalOpen] = useState(false);
	const transitionTimeout = useRef<any | undefined>(undefined);
	const floorStep = Math.floor(step);
	const floorPrevStep = Math.floor(prevStep);

	useEffect(() => {
		if (availabilityStore?.results || availabilityStore?.error) {
			setEditSelectionMode(true);
		}
	}, [availabilityStore, availabilityStore.results, availabilityStore.error]);

	const setStep = useCallback(
		(newStep: number) => {
			if (lockedStep) {
				if (lockedStep === 1 && newStep < 2) {
					setPrevStep(step);
					setStepState(newStep);
				}
			} else {
				setPrevStep(step);
				setStepState(newStep);
			}
		},
		[lockedStep, step]
	);

	const nextStep = useCallback(() => {
		setStep(Math.floor(step + 1));
	}, [setStep, step]);

	const toPrevStep = useCallback(() => {
		if (step <= 2) {
			setStep(startingStep.current);
		} else {
			setStep(Math.floor(step - 1));
		}
	}, [step, setStep, startingStep.current]);

	const openModal = () => {
		if (lockedStep && lockedStep === 1) {
			setStepState(1);
		}
		setModalOpen(true);
	};

	const closeModal = () => {
		setModalOpen(false);
	};

	const clickUpdateSelection = useCallback(() => {
		document.getElementById(updateButtonId)?.click();
	}, [lockedStep]);

	useEffect(() => {
		const handleCawComponentOpen = (e: Event) => {
			const event = e as CustomEvent;
			if (!lockedStep && !isDesktop) {
				if (event?.detail?.selectProperty) {
					planStore?.updateSearch({
						hotelCode: event.detail.selectProperty,
					});
				}
				if (event?.detail?.setFocusTo) {
					let step =
						['property', 'dates', 'occupancy', 'promoCode'].indexOf(event.detail.setFocusTo) + 1;
					if (step) {
						if (step > 1 && !planStore?.destination) {
							step = 1;
						}
						setStep(step);
					}
				}
				openModal();
			}
			setModalOpen(true);
		};
		window.addEventListener('cawComponentOpen', handleCawComponentOpen);
		return () => window.removeEventListener('cawComponentOpen', handleCawComponentOpen);
	}, [lockedStep, isDesktop, openModal, setStep]);

	const label = useMemo(() => {
		if (loading) {
			return '';
		} else if (step >= 1 && step < 2) {
			return 'Find Your Destination';
		} else if (step === 2) {
			return 'Choose Your Dates';
		} else if (step === 3) {
			return 'Add Number of Guests';
		} else if (step === 4) {
			return 'Plan Your Stay';
		} else {
			return 'Find Your Destination';
		}
	}, [step, loading]);

	useLayoutEffect(() => {
		const navSm = document.querySelector('.Navigation-sm') as HTMLElement;
		const isBookingFlow = document.querySelector('.booking-flow-mode') as HTMLElement;
		const filterOptions = document.querySelector('#RoomListing-floating-filter') as HTMLElement;
		if (!navSm || !filterOptions || !isBookingFlow) return;
		if (scrollDirection === 'down') {
			navSm.style.top = '-60px';
			filterOptions.style.top = '96px';
		} else {
			navSm.style.top = '0px';
			filterOptions.style.top = '156px';
		}
	}, [scrollDirection]);

	useLayoutEffect(() => {
		const navSm = document.querySelector('.Navigation-sm') || document.querySelector('.PPHeader');
		let firstElement = navSm?.parentElement?.nextElementSibling as HTMLElement | undefined;
		while (firstElement && firstElement?.tagName?.toLowerCase() !== 'div') {
			firstElement = firstElement.nextElementSibling as HTMLElement | undefined;
		}

		const shouldShowSummary =
			!lockedStep &&
			navSm &&
			editSelectionMode &&
			(availabilityStore.fetching || availabilityStore.results || availabilityStore.error);

		if (shouldShowSummary) {
			if (firstElement) {
				firstElement.style.marginTop = '100px';
			}
			setPortalTarget(navSm);
		} else {
			setPortalTarget(null);
		}

		return () => {
			if (!lockedStep && firstElement) {
				firstElement.style.marginTop = '';
			}
		};
	}, [
		editSelectionMode,
		lockedStep,
		availabilityStore.results,
		availabilityStore.error,
		availabilityStore.fetching,
	]);

	useEffect(() => {
		(document.activeElement as HTMLButtonElement)?.blur();
	}, [editSelectionMode, planStore, step]);

	useEffect(() => {
		if (transitionTimeout.current) {
			clearTimeout(transitionTimeout.current);
			transitionTimeout.current = undefined;
		}
		if (!lockedStep) {
			if (transitioningStep !== floorStep) {
				setTransitioningStep(step);
			} else {
				transitionTimeout.current = setTimeout(() => {
					setPrevStep(step);
				}, 500);
			}
		}
		return () => {
			if (transitionTimeout.current) {
				clearTimeout(transitionTimeout.current);
				transitionTimeout.current = undefined;
			}
		};
	}, [lockedStep, transitioningStep, step, prevStep]);

	const summaryContent = editSelectionMode && isABF && !lockedStep && (
		<div
			className="tailwind-root CAW"
			style={{ width: '100vw', minWidth: '100vw', position: 'relative', display: 'block' }}
		>
			<div
				className={classNames(
					'@container Summary-container bg-surface w-full flex justify-between p-150 max-w-[100vw] border-y border-y-border-subtle h-[96px]',
					{
						'-mx-[20px]': portalTarget && portalTarget.classList.contains('Navigation-sm'),
						'shadow-[0_0_20px_0_rgba(0,0,0,0.15)]':
							portalTarget && portalTarget.classList.contains('PPHeader'),
					}
				)}
			>
				<div className="Summary-field flex flex-col justify-between items-start w-full">
					<div className="flex flex-col spacing-stack-xs">
						<span className="Summary-content flex flex-row spacing-inline-2xs items-start justify-start">
							<span className="inline-flex h-[14px] w-[14px] shrink-0">
								<IconLocationPinLargeFill height={14} />
							</span>
							<span className="text-subtitle2 text-text-heading mt-[-2px]">
								<BevSafeText>
									{planStore?.destination?.shortName || planStore?.destination?.officialName}
								</BevSafeText>
							</span>
						</span>
						<div className="flex flex-col gap-[5px]">
							<div className="Summary-content">
								{/* prettier-ignore */}
								<span className="text-label text-text-primary">
							        {planStore?.search?.checkinDate &&
							        	format(planStore?.search?.checkinDate, 'MMM d')} -{' '}
							        {planStore?.search?.checkoutDate &&
							        	format(planStore?.search?.checkoutDate, 'MMM d')}{' '}
							        {planStore?.search?.flexDatesSelected
							        	? ` | ± ${planStore?.search.flexDatesSelected} ${translate('days')}`
							        	: undefined}
						        </span>
							</div>
							<div className="Summary-content">
								<label className="text-caption text-text-primary">{`${planStore?.search?.rooms?.length} ${
									planStore?.search?.rooms?.length === 1 ? translate('Room') : translate('Rooms')
								} | `}</label>
								<span className="text-caption text-text-primary">
									{planStore?.totalAdults}{' '}
									{(planStore?.totalAdults || 1) > 1 ? translate('Adults') : translate('Adult')}
									{planStore?.totalChildren ? (
										<>
											, {planStore?.totalChildren}{' '}
											{planStore?.totalChildren === 1
												? translate('Child')
												: translate('Children')}{' '}
										</>
									) : undefined}
								</span>
							</div>
						</div>
					</div>
				</div>
				<Button
					intent="unstyled"
					className="Summary-cta text-button2 w-[69px] !min-w-[69px] h-[35px] self-end rounded-[5px] CTA CTA--secondary--light"
					onClick={() => {
						setTransitioningStep(4);
						setStep(4);
						openModal();
					}}
				>
					{translate('Edit')}
				</Button>
			</div>
		</div>
	);

	return (
		<>
			{EditButton && (
				<EditButton
					open={() => {
						setEditSelectionMode(true);
						openModal();
					}}
				/>
			)}
			{portalTarget && summaryContent && createPortal(summaryContent, portalTarget)}
			{document.body &&
				modalOpen &&
				createPortal(
					<div className="@container tailwind-root CAW">
						<Modal
							modalPaddingClassNames={classNames('p-0 pb-250', {
								'spacing-card-padding-xs': (step !== 4 && step !== 3 && step >= 2) || isOffline,
								'spacing-card-padding-y-xs': (step === 4 || step < 2) && step !== 3 && !isOffline,
								'pb-0': step === 3 && !isOffline,
							})}
							focusContainerClassNames="!max-h-[90svh] !h-[90svh]"
							contentContainerClassNames="pt-0 justify-start"
							overlayClassName={classNames({
								'bg-transparent': step !== 4 && editSelectionMode,
							})}
							close={closeModal}
							ModalHeader={({ close }) => (
								<div className="flex flex-col w-full">
									<div className="h-[24px] min-h-[24px] flex flex-row justify-end items-center z-[60] spacing-modal-padding-x gap-100 mt-300 mb-200 mt-250 md:mt-300">
										{!isOffline && (
											<div className="w-full flex flex-row justify-center items-center pl-[24px]">
												<span className="text-center subtitle-1 text-text-heading ml-100">
													{translate(label)}
												</span>
											</div>
										)}
										{close && (
											<Button
												intent="unstyled"
												onClick={close}
												className="w-[24px] h-[24px] cursor-pointer"
											>
												{step !== 4 && editSelectionMode ? (
													<IconCaretDown width={24} height={24} />
												) : (
													<IconX width={24} height={24} />
												)}
												<span className="visually-hidden">{translate('close')}</span>
											</Button>
										)}
									</div>
									{/* Progress Bar */}
									<div
										className={classNames('h-[2px] w-full bg-border-subtle relative', {
											invisible: isOffline || editSelectionMode,
										})}
									>
										<div
											className={classNames('h-[2px] bg-border-strong absolute top-0 left-0', {
												'w-[25%]': step < 2,
												'w-[50%]': step === 2,
												'w-[75%]': step === 3,
												'w-full': step === 4,
											})}
										></div>
									</div>
								</div>
							)}
							ModalFooter={
								isOffline
									? undefined
									: ({ close }) => (
											<div
												ref={footerRef}
												className={classNames('flex flex-row items-center', {
													'spacing-card-padding-xs !pb-300': step >= 2,
													'!invisible !h-0 !p-0': lockedStep && step < 2,
													'border-t border-t-border-subtle': step !== 4,
													'justify-between': step !== startingStep.current,
													'justify-end':
														step === startingStep.current &&
														step >= 2 &&
														step !== 4 &&
														!editSelectionMode,
												})}
											>
												{step >= 2 && step !== 4 && !editSelectionMode && (
													<>
														{step !== startingStep.current && (
															<Button
																intent="text"
																onClick={toPrevStep}
																disabled={step !== prevStep}
															>
																{translate('Go back')}
															</Button>
														)}
														<Button
															intent="filled"
															className="min-w-[180px]"
															onClick={nextStep}
															disabled={step !== prevStep}
														>
															{translate('Next')}
														</Button>
													</>
												)}
												{step === 4 && (
													<Button
														intent="filled"
														className="w-full min-w-[180px]"
														disabled={
															!planStore?.search?.hotelCode ||
															!planStore?.search?.checkinDate ||
															!planStore?.search?.checkoutDate
														}
														onClick={() => {
															if (availabilityStore.error || availabilityStore.results) {
																triggerEvent('integrated booking engine', 'change search');
															}
															setEditSelectionMode(true);
															submitSearch();
															close?.();
														}}
													>
														{translate('Check Rates')}
													</Button>
												)}
												{step !== 4 && editSelectionMode && (
													<Button
														id={updateButtonId}
														intent="filled"
														className="CAW-update-button w-full min-w-[180px]"
														onClick={() => {
															close?.();
														}}
													>
														{translate('Update Selection')}
													</Button>
												)}
											</div>
										)
							}
							mobileAnimate
							mobileFullScreen={false}
							useNavBreakpoint={true}
						>
							<div
								ref={cawComponentRef}
								className={classNames('relative h-full', {
									singleProperty: isSingleProperty,
									horizontal: !isPPC,
									'ppc vertical': isPPC,
									hasDestinationPicker: !isSingleProperty,
									propertySubpage: isPropertySubpage,
									hasAlert: isOffline || error,
									isOffline: isOffline,
									'overflow-hidden': floorStep !== floorPrevStep,
								})}
							>
								{loading ? (
									<LoadingIndicator ppMode={ppMode} />
								) : !isOffline ? (
									<ErrorBoundary setError={setError}>
										{!lockedStep && floorStep !== floorPrevStep && (
											<section
												className={classNames('h-full w-full overflow-x-hidden absolute top-0 z-0')}
											>
												<CAWMobileDialogContent
													setStep={setStep}
													startingStep={startingStep.current}
													step={step}
													destinationPlaceholder={destinationPlaceholder}
													availabilityStore={availabilityStore}
													ppMode={ppMode}
													hidePromoCode={hidePromoCode}
													submitSearch={submitSearch}
												/>
											</section>
										)}
										<section
											ref={mainBodyRef}
											className={classNames('h-full w-full overflow-x-hidden bg-surface', {
												'transition-transform duration-500 absolute top-0':
													floorStep !== floorPrevStep,
												'translate-x-[100vw]': floorStep < floorPrevStep,
												'-translate-x-[100vw]': floorStep > floorPrevStep,
											})}
										>
											<CAWMobileDialogContent
												setStep={setStep}
												startingStep={startingStep.current}
												step={!lockedStep && floorPrevStep !== floorStep ? prevStep : step}
												isPrevStep={!lockedStep && floorPrevStep !== floorStep}
												propertiesArray={propertiesArray}
												destinationPlaceholder={destinationPlaceholder}
												availabilityStore={availabilityStore}
												ppMode={ppMode}
												hidePromoCode={hidePromoCode}
												submitSearch={submitSearch}
												updateSelection={clickUpdateSelection}
											/>
										</section>
									</ErrorBoundary>
								) : (
									<div>
										<Offline
											title={isOffline?.title}
											explanation={isOffline?.explanation}
											phone={isOffline.phone?.plainText}
											email={isOffline?.email}
											buttonText={isOffline.cta?.text}
											buttonLink={isOffline.cta?.link}
											isMobile={true}
										/>
									</div>
								)}
							</div>
						</Modal>
					</div>,
					document.body
				)}
		</>
	);
}
export default observer(CAWMobileDialog);
